import React, {useEffect, useState} from "react";
import Person from "../../model/Person";
import {Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme} from "@mui/material";
import Review, {ReviewChecklistItem, ReviewItem, ReviewStatus} from "../../model/Review";
import Model from "../../model/Model";
import {createUUID, humanizeString, isoToLocalDateString} from "../../stores/StoreUtilities";
import {Gender, UpdateReviewInput} from "../../API";
import {FemaleIcon, MaleIcon} from "../../styles/Icons";
import EditIcon from "@mui/icons-material/Edit";
import PersonEditDialog from "../calc/timeline/PersonEditDialog";
import IconicButton from "../../components/controls/IconicButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Visible from "../../components/Visible";
import IModelItem from "../../model/IModelItem";
import DialogButton from "../../components/form/DialogButton";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {useResources} from "../../stores/ResourceProvider";


const TimelineReview = ({
  review,
  model,
  updatedAt,
  isDialog,
  onChange,
  onChangeView,
  onClose,
  onDecline,
} : {
  review: Review
  model: Model
  updatedAt: string
  isDialog?: boolean
  onChange?(item: IModelItem): void
  onChangeView?(view: string): void
  onClose?(): void
  onDecline?(review: Review): void
}) => {
  const [persons, setPersons] = useState<Person[]>([])
  const [editPerson, setEditPerson] = useState<Person | undefined>()

  const {userStore} = useResources()
  const theme = useTheme()

  useEffect(() => {
    if (model) {
      const filtered = model.persons.filter((p: Person) => p.id !== model.jointId)
      Model.sortPersons(filtered)
      setPersons(filtered)
    }
  }, [model, updatedAt])

  const handleAddPerson = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (model) {
      const newPerson = new Person({
        id: createUUID(),
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        nickname: `Person ${model.persons.length + 1}`,
      })
      setPersons([...persons, newPerson])
      setEditPerson(newPerson)
      if (onChange) {
        onChange(newPerson)
      }
    }
  }

  const handleSavePerson = async (person: Person) => {
    const index = persons.findIndex((p: Person) => p.id === person.id)
    if (index >= 0) {
      persons[index] = person
      setPersons([...persons])
    }
    if (onChange) {
      onChange(person)
    }
  }

  const handleDeletePerson = (person: Person) => {
    const index = persons.findIndex((p: Person) => p.id === person.id)
    if (index >= 0) {
      persons.splice(index, 1)
      setPersons([...persons])
    }
    if (onChange) {
      onChange(person)
    }
  }

  const handleSave = async () => {
    await updateChecklist(ReviewItem.Timeline, true)
  }

  const updateChecklist = async (item: ReviewItem, checked: boolean) => {
    if (review.checklist) {
      const index = review.checklist.findIndex((checklistItem: ReviewChecklistItem) => checklistItem.reviewItem === item)
      if (index >= 0) {
        review.checklist[index].completed = checked
        const input: UpdateReviewInput = {
          id: review.id,
          checklist: JSON.stringify(review.checklist),
          status: ReviewStatus.InProgress
        }

        await userStore.updateReview(input)
      }
    }
  }

  const handlePrev = async (event: any) => {
    await handleSave()
    if (onChangeView) {
      onChangeView("checklist")
    }
  }

  const handleNext = async (event: any) => {
    await handleSave()
    if (onChangeView) {
      onChangeView("networth")
    }
  }

  const handleCancel = async (event: any) => {
    const input: UpdateReviewInput = {
      id: review.id,
      status: ReviewStatus.InProgress
    }

    const update = await userStore.updateReview(input)
    if (update && onChange) {
      onChange(update)
    }
    if (update && onClose) {
      onClose()
    }
  }

  const handleDecline = async (event: any) => {
    if (onDecline) {
      onDecline(review)
    }
  }

  const renderPerson = (person: Person, index: number) => {
    const icon = person.gender === Gender.Male ? <MaleIcon/> : <FemaleIcon/>
    // const birthDate = isoToLocalDate(person.birthDate)
    // const gender = person.gender
    // const maritalStatus = person.maritalStatus
    // const retireDate = isoToLocalDate(person.retireDate)
    // const hereditaryAdjust = isNumber(person.hereditaryAdjust) ? String(person.hereditaryAdjust) : ""

    return (
      <TableRow>
        <TableCell>{icon}</TableCell>
        <TableCell>{person.nickname}</TableCell>
        <TableCell>
          <IconButton id={`${person.id}-edit-btn`}size="small"
                      sx={{paddingTop:0, paddingBottom:0}}
                      onClick={(event: any) => {
                        setEditPerson(person)
                      }}
          >
            <EditIcon sx={{width:18, height:18}}/>
          </IconButton>
        </TableCell>
        <TableCell>{humanizeString(person.gender)}</TableCell>
        <TableCell>{humanizeString(person.maritalStatus)}</TableCell>
        <TableCell>{isoToLocalDateString(person.birthDate)}</TableCell>
        <TableCell>{isoToLocalDateString(person.retireDate)}</TableCell>
        <TableCell>{person.hereditaryAdjust}</TableCell>
      </TableRow>
    )
  }

  const canFinishLater = onClose && !review.submittedOn
  const canDecline = onDecline && !review.managed
  const height = isDialog ? "calc(100vh - 220px)" : "calc(100vh - 140px)"

  return (
    <React.Fragment>
      <DialogContent sx={{ my:0, p:0, height:height, maxHeight:height, overflow:"scroll"}}>
        <Box sx={{display:"flex", flexDirection:"column", alignItems:"flex-start", mt:1, pl:0, pb:0, mb:1}}>
          <Typography variant="h2" color="primary" gutterBottom>Timeline Review</Typography>
          <Typography variant="body2" gutterBottom>
            Please update the people and timeline for your retirement.
          </Typography>
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Typography variant="h5">People</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40}></TableCell>
                <TableCell>Nickname</TableCell>
                <TableCell width={32}></TableCell>
                <TableCell width={120}>Gender</TableCell>
                <TableCell width={140}>Marital Status</TableCell>
                <TableCell width={120}>Birth Date</TableCell>
                <TableCell width={150}>Est Retire Date</TableCell>
                <TableCell width={130}>Life Exp Adj</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {persons.map((person: Person, index: number) =>
                renderPerson(person, index)
              )}
            </TableBody>
          </Table>
          <Visible cond={persons.length < 2}>
            <Box display="flex" justifyContent="flex-start" ml={2.5}>
              <IconicButton onClick={handleAddPerson} label="Add Person">
                <AddCircleIcon sx={{color: theme.palette.secondary.light}}/>
              </IconicButton>
            </Box>
          </Visible>

        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="center">
          <Box display="flex" justifyContent="flex-start">
            {canFinishLater &&
              <DialogButton variant="secondary" onClick={handleCancel}>
                Sign Out & Finish Later
              </DialogButton>
            }
            {canDecline &&
              <DialogButton variant="secondary" spacing="left" onClick={handleDecline}>
                Decline
              </DialogButton>
            }
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <DialogButton variant="secondary" onClick={handlePrev}>
            Prev
          </DialogButton>
          <DialogButton variant="primary" spacing="left" onClick={handleNext}>
            Next
          </DialogButton>
        </Box>
      </DialogActions>

      {editPerson &&
        <PersonEditDialog person={editPerson} model={model}
                          open={true}
                          onSave={(person: Person) => {
                            setEditPerson(undefined)
                            handleSavePerson(person)
                          }}
                          onDelete={(person: Person) => {
                            setEditPerson(undefined)
                            handleDeletePerson(person)
                          }}
                          onClose={() => {
                            setEditPerson(undefined)
                            if (!editPerson.createdAt) {
                              handleDeletePerson(editPerson)
                            }
                          }}/>
      }
    </React.Fragment>
  )
}

export default TimelineReview